import hash from "@emotion/hash"
import styled from "@emotion/styled"
import { Icon } from "components/icon/Icon"
import { useConsole } from "contexts/Console"
import { useDictionary } from "contexts/Dictionary"

import { useNavigation } from "contexts/Navigation"
import { useWishlist } from "contexts/Wishlist"
import getMediaQuery from "css/breakpoints"
import { body50, bold, legend100, normal, visuallyHidden } from "css/text"

import useSSR from "hooks/useSSR"
import { startTransition, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useNav } from "components/nav-v2/Wrapper"

const cssOn = `css-${hash("wishlist:toggle:on")}`
const cssOff = `css-${hash("wishlist:toggle:off")}`

const cssWishlistNotificationVisible = `css-${hash("menu:wishlistnotification:visible")}`

const Root = styled.div`
  display: grid;
`

const Button = styled.a`
  all: unset;
  cursor: pointer;
  user-select: none;
  grid-area: 1/1;
  display: grid;
  position: relative;
  align-items: center;
  grid-template-columns: minmax(15px, auto) 1fr;
  padding: 0.8rem 0.6rem;
  color: rgb(var(--light-black));

  ${getMediaQuery("xxl", { min: true })} {
    grid-gap: 1ex;
  }

  [dir="rtl"] & {
    left: calc(-1 * var(--touch-padding));
    right: auto;
  }

  &[disabled] {
    cursor: default;
    pointer-events: none;
  }
`

const On = styled(Button)`
  z-index: 1;
  opacity: 1;
  pointer-events: auto;

  > span {
    transition: color 300ms;
    will-change: color;
  }

  &[data-active="true"] {
    pointer-events: none;
    color: rgb(var(--green));
  }
`

const ButtonContainer = styled.div`
  &,
  & * {
    white-space: nowrap !important;
  }
  display: flex;

  > a {
    ${legend100}
    ${bold}
    padding: 0.8rem 0.5rem;
    transition: color 300ms;

    > div > svg {
      width: 15px;
      height: 15px;
      fill: currentColor;

      ${getMediaQuery("m")} {
        width: inherit;
        height: inherit;
      }
    }

    &:hover {
      @media (hover: hover) and (pointer: fine) {
        color: rgb(var(--green));
      }
    }
  }

  .text {
    ${normal}
    ${visuallyHidden}
    opacity: 0;
    transition: opacity 300ms;

    ${getMediaQuery("m")} {
      clip: auto;
      clip-path: none;
      display: flex;
      height: auto;
      overflow: visible;
      position: static;
      width: 0;
    }

    ${getMediaQuery("xxl", { min: true })} {
      opacity: min(var(--m, 0), 1);
    }
  }
`

const NotificationAdd = styled.div`
  --padding-inline: 1.875rem;
  --padding-block: 1.25rem;

  position: absolute;
  top: calc(100% + 10px);
  left: 50%;

  width: 70%;
  padding-inline: var(--padding-inline);
  padding-block: var(--padding-block);
  border-radius: 0.5rem;

  background-color: rgb(var(--pure-white));
  box-shadow: 0px 5px 10px rgba(0, 0, 10, 0.2);

  pointer-events: all;
  opacity: 0;
  transform: translate(-50%, 40px);
  transition: all 0.3s ease-in-out;

  &.${cssWishlistNotificationVisible} {
    opacity: 1;
    transform: translate(-50%, 20px);
  }

  ${getMediaQuery("m")} {
    left: unset;
    inset-inline-end: var(--outer-margin);
    width: 340px;
    padding-block: 1.25rem;
    transform: translate(0, 40px);

    &.${cssWishlistNotificationVisible} {
      transform: translate(0, 20px);
    }
  }

  > h2 {
    ${body50}
    color: rgb(var(--green));
  }

  > p {
    ${legend100}
    white-space: normal !important;
    overflow-wrap: break-word;
  }

  > a {
    ${legend100}
    ${bold}
    color: rgb(var(--green));
    text-decoration: none;

    display: block;
    margin-block-start: 1.25rem;

    ${getMediaQuery("m")} {
      margin-block-start: 0.625rem;
    }
  }
`

const Heart = styled.div`
  display: flex;
  position: relative;

  .count {
    color: rgb(var(--pure-white));
    position: absolute;
    top: -0.5rem;
    left: 0.5rem;
    width: 1.1rem;
    height: 1.1rem;
    border-radius: 50%;
    z-index: 100;

    [dir="rtl"] & {
      right: auto;
      left: -0.625rem;
    }

    display: flex;
    justify-content: center;
    align-items: center;

    ${bold}
    font-size: 10px;
    background-color: rgb(50 144 102);
  }
`

export default function MenuToggle(props) {
  const console = useConsole()
  const navigation = useNavigation()
  const dictionary = useDictionary()
  const ssr = useSSR()
  const nav = useNav()

  const [active, setActive] = useState(true)
  const [isWatch, setIsWatch] = useState(false)
  const [isNotificationOpen, setIsNotificationOpen] = useState(false) // true when the wishlist notification is in the DOM (opacity: 0)
  const [isNotificationVisible, setIsNotificationVisible] = useState(false) // true when the wishlist notification is visible (opacity: 1)
  const [notificationContent, setNotificationContent] = useState({ action: null })

  const rroot = useRef()

  const { wishlist, notificationRMC } = useWishlist()

  const href = navigation.localize(`/wishlist`)
  const navigate = e => {
    e.preventDefault()
    navigation.push(href)
  }

  useLayoutEffect(() => nav.on("main:lock", () => startTransition(() => setActive(false))))
  useLayoutEffect(() => nav.on("main:unlock", () => startTransition(() => setActive(true))))

  useEffect(() => {
    // This allows the animation to play entirely before the wishlist is removed from DOM
    if (notificationRMC) {
      setNotificationContent(notificationRMC)
      setIsNotificationOpen(true)
      setIsWatch(notificationRMC?.rmc?.startsWith("m"))
      setTimeout(() => {
        setIsNotificationVisible(true)
      }, 0)
    } else {
      setIsNotificationVisible(false)
      setTimeout(() => {
        setIsNotificationOpen(false)
      }, 300)
    }
  }, [notificationRMC])

  console.verbose("Wishlist:Toggle(%o)", notificationRMC, wishlist, { active })
  return (
    <Root ref={rroot} className={active ? cssOn : cssOff}>
      <ButtonContainer>
        <On
          as='a'
          type='a'
          href={navigation?.slug !== "/wishlist" ? href : ""}
          onClick={navigate}
          data-active={navigation.slug.includes("/wishlist")}
          aria-current={navigation.slug.includes("/wishlist") ? "page" : undefined}
          aria-hidden={!active || navigation.slug === "/wishlist" ? "true" : "false"}
          tabIndex={!active || navigation.slug === "/wishlist" ? -1 : 0}
          style={{ "--m": ssr ? 0 : 1 }}
        >
          <Heart>
            {wishlist?.length > 0 && (
              <div className='count'>
                <span>{wishlist?.length}</span>
              </div>
            )}
            <Icon size='18' type={"heartFull"} />
          </Heart>
          <span className='text'>{dictionary.myWatches()}</span>
        </On>
        <div aria-live='polite'>
          {isNotificationOpen ? (
            <NotificationAdd
              className={isNotificationVisible ? cssWishlistNotificationVisible : ""}
              // onMouseEnter={onNotificationMouseEnter}
              // onMouseLeave={onNotificationMouseLeave}
              // Hide notification when on wishlist page
              style={{ opacity: navigation.slug === "/wishlist" ? 0 : 1 }}
            >
              {notificationContent?.action === "remove" ? (
                <>
                  <h2>{isWatch ? dictionary.headerWishlistWatchRemovedTitle() : dictionary.headerWishlistAccessoriesRemovedTitle()}</h2>
                  <p>{isWatch ? dictionary.headerWishlistWatchRemovedText() : dictionary.headerWishlistAccessoriesRemovedText()}</p>
                </>
              ) : (
                <>
                  <h2>{isWatch ? dictionary.headerWishlistWatchAddedTitle() : dictionary.headerWishlistAccessoriesAddedTitle()}</h2>
                  <p>{isWatch ? dictionary.headerWishlistWatchAddedText() : dictionary.headerWishlistAccessoriesAddedText()}</p>
                </>
              )}
              {/* <Link href='/wishlist' className='inline green' aria-disabled={true}>
              {dictionary.headerWishlistCta()}
            </Link> */}
            </NotificationAdd>
          ) : (
            <div style={{ visibility: "hidden" }} />
          )}
        </div>
      </ButtonContainer>
    </Root>
  )
}
